<template>
  <v-dialog
    v-model="isOpened"
    data-qa="core-dialog"
    :width="DIALOG_WIDTHS[size]"
    :fullscreen="modalSizes.fullscreen"
    v-bind="{
      ...$attrs,
      'content-class': $attrs['content-class']
        ? `${$attrs['content-class']} flex-dialog`
        : 'flex-dialog',
    }"
    v-on="$listeners"
  >
    <div
      :data-qa="dataQa"
      data-qa-base="core-dialog"
      class="core-dialog d-flex flex-column flex-grow-1 overflow-y-auto bg-gray-0 elevation-20 h-100 position-relative"
      :class="{
        'core-dialog--sm': modalSizes.sm,
        'core-dialog--md': modalSizes.md,
        'core-dialog--fullscreen': modalSizes.fullscreen,
        'rounded-sm': !modalSizes.fullscreen,
      }"
      :style="{
        height: getSize(height),
        maxHeight: getSize(maxHeight),
        minHeight: getSize(minHeight),
      }"
    >
      <div
        v-if="title || $slots.prependBtn || $scopedSlots.prependBtn || $slots.title || $slots.appendBtn || $slots.extension"
        data-qa="core-dialog-toolbar-container"
        class="core-dialog__container flex-shrink-0"
      >
        <div
          data-qa="core-dialog-toolbar"
          class="core-dialog__toolbar d-flex flex-column justify-center ga-2"
          :class="{ 'pb-0': !divider && $slots.default }"
        >
          <div
            v-if="title || $slots.prependBtn || $scopedSlots.prependBtn || $slots.title || $slots.appendBtn"
            class="core-dialog__header d-flex align-center text-break ga-9"
          >
            <slot
              name="prependBtn"
              :btn-props="PREPEND_BTN_PROPS"
            />
            <div
              v-if="title || $slots.title"
              data-qa="core-dialog-title"
              class="d-flex align-center text-h6 text--text mr-auto"
            >
              {{ title }}
              <slot name="title" />
            </div>

            <slot name="appendBtn" />
          </div>

          <slot name="extension" />
        </div>
      </div>

      <v-divider
        v-if="divider"
        class="border-t-sm"
      />

      <div
        v-if="$scopedSlots.default || $slots.default"
        data-qa="core-dialog-content"
        class="core-dialog__container d-flex flex-column flex-fill overflow-y-auto custom-scroll"
      >
        <div
          class="d-flex flex-column flex-fill py-4"
          :class="[modalSizes.fullscreen ? 'px-4' : 'px-6', slotContentClass]"
        >
          <slot
            :x-space-class="modalSizes.fullscreen ? 'px-4' : 'px-6'"
            :counter-x-space-class="modalSizes.fullscreen ? 'mx-n4' : 'mx-n6'"
          />
        </div>
      </div>

      <div
        v-if="$slots.actions"
        data-qa="core-dialog-actions-container"
        class="core-dialog__container flex-shrink-0 mt-auto"
        :class="{
          'elevation-2': modalSizes.fullscreen,
        }"
      >
        <div
          data-qa="core-dialog-actions"
          class="core-dialog__actions d-flex align-center justify-end"
          :class="{
            'ga-2 pa-2': modalSizes.sm || modalSizes.md || modalSizes.fullscreen,
            'ga-4 pt-2 px-6 pb-6': modalSizes.lg || modalSizes.xl,
          }"
        >
          <slot name="actions" />
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, watch } from 'vue'
import scrollLock from 'src/utils/scrollLock'

const PREPEND_BTN_PROPS = {
  icon: true,
  'data-qa': 'core-dialog-prepend-btn',
}

const DIALOG_WIDTHS = {
  sm: 304,
  md: 360,
  lg: 456,
  xl: 650,
  fullscreen: undefined,
} as const

const props = defineProps<{
  value: boolean
  size: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen'
  title?: string
  divider?: boolean
  dataQa?: string
  height?: string | number
  maxHeight?: string | number
  minHeight?: string | number
  slotContentClass?: string | string[]
}>()

type Emits = {
  (_event: 'input', _value: boolean): void,
}

const emit = defineEmits<Emits>()

const isOpened = computed({
  get() {
    return props.value
  },
  set(value: boolean) {
    emit('input', value)
  },
})

watch(isOpened, value => {
  if (value) {
    scrollLock.disableScroll()
  } else {
    scrollLock.enableScroll()
  }
})

onBeforeUnmount(() => {
  if (isOpened.value) {
    scrollLock.enableScroll()
  }
})

const modalSizes = computed(() => ({
  sm: props.size === 'sm',
  md: props.size === 'md',
  lg: props.size === 'lg',
  xl: props.size === 'xl',
  fullscreen: props.size === 'fullscreen',
}))

function getSize(size?: number | string) {
  if (typeof size === 'number') {
    return `${size}px`
  }

  if (typeof size === 'string') {
    return Number.isNaN(Number(size))
      ? size
      : `${size}px`
  }

  return undefined
}
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

:deep(.v-dialog.flex-dialog) {
  display: flex;
  flex-direction: column;
}

.core-dialog {
  &__container {
    position: relative;

    & > :deep(*) {
      width: 100%;
      max-width: 720px;
      margin-left: auto;
      margin-right: auto;
    }
  }

  & > .core-dialog__container {
    & > .core-dialog__toolbar {
      min-height: 56px;
      padding: 12px 24px;

      & > .core-dialog__header :deep(.v-btn) {
        color: $ui-kit-icon-secondary;
        margin: -4px -8px;
        height: 32px;
        width: 32px;
      }
    }

    & > .core-dialog__actions {
      & > :deep(.v-btn) {
        padding-left: 16px;
        padding-right: 16px;
      }
    }
  }

  &--sm > .core-dialog__container {
    & > .core-dialog__toolbar {
      min-height: auto;
      padding: 24px;
    }

    & > .core-dialog__actions {
      & > :deep(.v-btn) {
        padding-left: 8px;
        padding-right: 8px;
      }
    }
  }

  &--md > .core-dialog__container {
    & > .core-dialog__actions {
      & > :deep(.v-btn) {
        padding-left: 8px;
        padding-right: 8px;
      }
    }
  }

  &--fullscreen > .core-dialog__container {
    & > .core-dialog__toolbar {
      padding: 12px 16px;
    }
  }
}

.custom-scroll {
  @supports (scrollbar-color: auto) {
    scrollbar-color: $ui-kit-bg-gray-80 $ui-kit-bg-gray-40;
  }

  @supports (selector(::-webkit-scrollbar)) {
    scrollbar-color: auto;

    &::-webkit-scrollbar-thumb {
      border-radius: $border-radius-pill;
      background-color: $ui-kit-bg-gray-60;
    }

    &::-webkit-scrollbar {
      width: 8px;
      border-radius: $border-radius-pill;
      background-color: $ui-kit-bg-gray-40;
    }
  }
}
</style>
